import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/common/StatusBadge";
import { Plus, ArrowRight, Trash2, Coins } from "lucide-react";
import { formatEuro, formatDateFr } from "@/lib/format";
import { toast } from "sonner";
import { FLOW_NATURE_LABELS, FLOW_STATUS_LABELS } from "@/constants/group";

const EMPTY = {
  from_company_id: "", to_company_id: "", date: new Date().toISOString().slice(0, 10),
  fiscal_year: new Date().getFullYear(), nature: "dividende", amount: 0,
  status: "planned", comment: "",
};

export default function IntragroupFlowsTab() {
  const [flows, setFlows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });

  const load = async () => {
    setLoading(true);
    try {
      const [f, c] = await Promise.all([api.get("/intragroup-flows"), api.get("/companies")]);
      setFlows(f.data.items || []); setCompanies(c.data.items || []);
    } catch {} setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const companyName = (id) => companies.find((c) => c.id === id)?.denomination || companies.find((c) => c.id === id)?.name || "—";

  const create = async () => {
    try {
      await api.post("/intragroup-flows", { ...form, amount: parseFloat(form.amount) });
      toast.success("Flux enregistré");
      setOpen(false); setForm({ ...EMPTY }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce flux ?")) return;
    try { await api.delete(`/intragroup-flows/${id}`); toast.success("Flux supprimé"); load(); }
    catch { toast.error("Suppression impossible"); }
  };

  const totalByStatus = flows.reduce((acc, f) => {
    acc[f.status] = (acc[f.status] || 0) + Number(f.amount || 0);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <StatBox label="Réalisé" value={totalByStatus.realized} tone="success" />
        <StatBox label="Validé" value={totalByStatus.validated} tone="info" />
        <StatBox label="Prévu" value={totalByStatus.planned} tone="warning" />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{flows.length} flux au total</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-pill" data-testid="flow-add-btn"><Plus className="mr-2 h-4 w-4" /> Nouveau flux</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Nouveau flux intragroupe</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Société émettrice</Label>
                  <Select value={form.from_company_id} onValueChange={(v) => setForm({ ...form, from_company_id: v })}>
                    <SelectTrigger data-testid="flow-form-from"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.denomination || c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Société bénéficiaire</Label>
                  <Select value={form.to_company_id} onValueChange={(v) => setForm({ ...form, to_company_id: v })}>
                    <SelectTrigger data-testid="flow-form-to"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.denomination || c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div>
                  <Label>Exercice</Label>
                  <Input type="number" value={form.fiscal_year} onChange={(e) => setForm({ ...form, fiscal_year: parseInt(e.target.value) || null })} />
                </div>
                <div>
                  <Label>Montant (€)</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="flow-form-amount" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nature</Label>
                  <Select value={form.nature} onValueChange={(v) => setForm({ ...form, nature: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(FLOW_NATURE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Statut</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(FLOW_STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Commentaire</Label>
                <Textarea rows={2} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={create} data-testid="flow-form-submit">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-6 py-4">Date</th>
              <th className="px-6 py-4">Flux</th>
              <th className="px-6 py-4">Nature</th>
              <th className="px-6 py-4 text-right">Montant</th>
              <th className="px-6 py-4">Statut</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && <tr><td colSpan={6} className="px-6 py-10 text-center text-muted-foreground">Chargement…</td></tr>}
            {!loading && flows.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-16 text-center text-muted-foreground">
                <Coins className="mx-auto h-8 w-8 mb-3 text-muted-foreground/60" />
                Aucun flux intragroupe.
              </td></tr>
            )}
            {flows.map((f) => (
              <tr key={f.id} data-testid={`flow-row-${f.id}`} className="hover:bg-secondary/30">
                <td className="px-6 py-3 text-xs font-mono text-muted-foreground">{formatDateFr(f.date)}</td>
                <td className="px-6 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{companyName(f.from_company_id)}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-medium">{companyName(f.to_company_id)}</span>
                  </div>
                  {f.comment && <div className="text-xs text-muted-foreground italic mt-0.5">{f.comment}</div>}
                </td>
                <td className="px-6 py-3">{FLOW_NATURE_LABELS[f.nature] || f.nature}</td>
                <td className="px-6 py-3 text-right font-mono">{formatEuro(f.amount, true)}</td>
                <td className="px-6 py-3">
                  <StatusBadge tone={f.status === "realized" ? "success" : f.status === "validated" ? "info" : "warning"}>
                    {FLOW_STATUS_LABELS[f.status]}
                  </StatusBadge>
                </td>
                <td className="px-6 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => del(f.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatBox({ label, value, tone }) {
  const bg = tone === "success" ? "bg-emerald-50" : tone === "info" ? "bg-sky-50" : "bg-amber-50";
  const text = tone === "success" ? "text-emerald-800" : tone === "info" ? "text-sky-800" : "text-amber-800";
  return (
    <div className={`rounded-2xl border border-border p-5 ${bg}`}>
      <div className={`text-xs font-bold uppercase tracking-widest ${text}`}>{label}</div>
      <div className="mt-1 font-serif text-2xl">{formatEuro(value || 0)}</div>
    </div>
  );
}
